/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../../root'
import { useModal } from '../../../contexts/ModalContext'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import FsiPropertiesModal from '../../../pages/browser/properties/FsiPropertiesModal'
import AccessRightsModal from '../../../pages/browser/access-rights/AccessRightsModal'
import MakeObsoleteModal from '../../../pages/browser/MakeObsoleteModal'
import FolderAndDocumentDetailsModal from '../../../components/modals/FolderAndDocumentDetailsModal'
import UpdateTrialsModal from './UpdateTrialsModal'
import { AclLevel, FsiLifecycleState, SystemFolderType } from '../../../constants/fsi'
import { MasterDataType } from '../../../constants/etmf'
import type { EtmfListViewValue } from '../../../constants/etmf'
import { capitalizeString } from '../../../utils/common'

// Access table for role checking - matches legacy accessService.rightsTable.js GROUPS
const accessTable = {
  rcmAdmins: ['ADMIN', 'CONTENT_ADMIN'],
  stagingManagers: ['ETMF_MANAGER', 'ETMF_SPECIALIST'],
  rcmUsers: ['POWER_USER', 'USER'],
  stagingUsers: ['ETMF_SITE_USER', 'ETMF_STUDY_USER']
}

interface EtmfActionsMenuProps {
  item: any
  listView: EtmfListViewValue
}

/**
 * Actions menu for ETMF Master Data tables (Products, Studies, Environments, Countries, Sites)
 * Loads file details dynamically when menu opens (similar to legacy MDFTableMenu.jsx)
 */
export const EtmfActionsMenu: React.FC<EtmfActionsMenuProps> = ({ item, listView }) => {
  const context = useServiceContext()
  const { showModal } = useModal()
  const {
    service: { access, checkmate },
    api
  } = context

  const [isOpen, setIsOpen] = useState(false)
  const [optionsLoaded, setOptionsLoaded] = useState(false)
  const [items, setItems] = useState<any[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const configureItems = (file: any) => {
    const menu: any[] = []

    // View Details - shown first for TRIAL type
    if (file.MDFType === MasterDataType.TRIAL) {
      menu.push({
        value: 'BROWSER_ACTION_VIEW_DETAILS',
        action: () => {
          showModal(<FolderAndDocumentDetailsModal fileDetails={file} versionInfo={{}} />)
        }
      })

      // Edit in Checkmate - for TRIAL with Checkmate integration
      if (checkmate.isCheckmated() && file.mccObjUuid) {
        menu.push({
          value: 'ETMF_ACTION_EDIT_CA_STUDIES',
          action: () => {
            const url = checkmate.getStudyUrl(file.mccObjUuid)
            window.open(url, '_blank')
          }
        })
      }
    }

    // Show File / RCM Attributes
    if (
      !file.workflow &&
      file.systemFolderType !== SystemFolderType.WORKFLOWS &&
      (access.isAllowed('fsiProperties') ||
        (access.isAllowed(['CONTENT_ADMIN', 'POWER_USER', 'USER', 'AUDITOR', accessTable.stagingManagers] as any) &&
          [AclLevel.READ, AclLevel.WRITE, AclLevel.DELETE].includes(file.acl)))
    ) {
      menu.push({
        value: file.MDFType === MasterDataType.TRIAL ? 'BROWSER_ACTION_RCM_ATTRIBUTES' : 'BROWSER_ACTION_SHOW_FILE',
        action: () => {
          showModal(
            <FsiPropertiesModal
              fileId={file?.id}
              isMDF={true}
              MDFType={file?.MDFType}
              isModal={true}
              fsiDetails={file}
            />
          )
        }
      })
    }

    // View Details - shown after Show File for non-TRIAL types
    if (file.MDFType !== MasterDataType.TRIAL) {
      menu.push({
        value: 'BROWSER_ACTION_VIEW_DETAILS',
        action: () => {
          showModal(<FolderAndDocumentDetailsModal fileDetails={file} versionInfo={{}} />)
        }
      })
    }

    // Access Rights / ACLs
    if (
      !file.workflow &&
      file.systemFolderType !== SystemFolderType.WORKFLOWS &&
      (access.isAllowed('fsiSettings') ||
        (access.isAllowed(['CONTENT_ADMIN', accessTable.stagingManagers] as any) &&
          [AclLevel.DELETE].includes(file.acl))) &&
      !file.isBeforeApproveStep &&
      file.state !== capitalizeString(FsiLifecycleState.DRAFT)
    ) {
      menu.push({
        value: 'BROWSER_ACTION_ACLS',
        action: () => {
          showModal(<AccessRightsModal fsi={file} />)
        }
      })
    }

    // Edit in Checkmate - for SITE with Checkmate integration
    if (checkmate.isCheckmated() && file.mccObjUuid && file.MDFType === MasterDataType.SITE) {
      menu.push({
        value: 'ETMF_ACTION_EDIT_CA_SITES',
        action: () => {
          const url = checkmate.getSiteUrl(file.mccParentObjUuid, file.mccObjUuid)
          window.open(url, '_blank')
        }
      })
    }

    // Update MDF - different label based on type
    menu.push({
      value: (() => {
        switch (file.MDFType) {
          case MasterDataType.SITE:
            return 'ETMF_ACTION_UPDATE_SITES'
          case MasterDataType.COUNTRY:
            return 'ETMF_ACTION_UPDATE_COUNTRIES'
          case MasterDataType.PRODUCT:
            return 'ETMF_ACTION_UPDATE_PRODUCTS'
          case MasterDataType.ENVIRONMENT:
            return 'ETMF_ACTION_UPDATE_ENVIRONMENTS'
          case MasterDataType.TRIAL:
            return 'ETMF_ACTION_UPDATE_STUDIES'
          default:
            return 'ETMF_ACTION_UPDATE'
        }
      })(),
      action: () => {
        showModal(<UpdateTrialsModal fsiId={file.id} type={file.MDFType} />)
      }
    })

    // Make Obsolete
    if (file.actionFlags?.canObsolete) {
      menu.push({
        value: 'BROWSER_ACTION_MAKE_OBSOLETE',
        action: () => {
          showModal(<MakeObsoleteModal fileDetails={file} />)
        }
      })
    }

    setItems(menu)
  }

  useEffect(() => {
    if (isOpen && !optionsLoaded) {
      const promises: Promise<any>[] = [api.files.getDetails(item.id)]

      if (checkmate.isCheckmated()) {
        promises.push(api.files.getFsiPath(item.id))
      }

      Promise.all(promises)
        .then((data: any[]) => {
          const fileDetails = data[0]?.plain ? data[0].plain() : data[0]
          const pathData = data[1] && Array.isArray(data[1]) && data[1].length ? data[1][data[1].length - 1] : null

          const enrichedFile = {
            ...item,
            ...fileDetails,
            ...(pathData || {})
          }

          configureItems(enrichedFile)
          setOptionsLoaded(true)
        })
        .catch((error) => {
          console.error('Error loading action menu options:', error)
          setItems([
            {
              value: 'ERROR_LOADING_OPTIONS',
              action: () => {},
              disabled: true
            }
          ])
          setOptionsLoaded(true)
        })
    }
  }, [isOpen, optionsLoaded, item, listView, api, checkmate, access, showModal])

  return (
    <ActionsMenu
      menuId={`action-menu-mdf-${item.id}`}
      onToggle={(open) => {
        setIsOpen(open)
      }}
      items={items}
    />
  )
}
